// Sortable tree built on `dnd-kit-sortable-tree`. The library handles
// the drag mechanics, projection from pointer X for indent, drop
// indicator rendering, and ghost-row placement at the projected
// position. We supply data + a row renderer.
//
// Gesture summary (this is the standard dnd-kit tree pattern):
//   • Drag a row up/down to reposition among siblings.
//   • Drag right to push it into the previous row (when that row is a
//     folder). The blue indicator jumps to the new indent so you see
//     where it'll land before releasing.
//   • Drag left to pop out one level.

import { forwardRef, useMemo, useState, type ReactNode } from "react";
import {
  SortableTree as LibSortableTree,
  FolderTreeItemWrapper,
  type TreeItem,
  type TreeItemComponentProps,
} from "dnd-kit-sortable-tree";

export type TreeKind = "folder" | "request" | "webhook";

export interface SourceFolder {
  id: string;
  parent_folder_id: string | null;
  name: string;
  sort_order?: number;
}
export interface SourceRequest {
  id: string;
  folder_id?: string | null;
  name: string;
  method: string;
  url?: string;
  sort_order?: number;
}
// Webhooks share folder placement + ordering with requests. The token
// powers the row's URL chip; request_count drives the "N captured" pill.
export interface SourceWebhook {
  id: string;
  folder_id?: string | null;
  name: string;
  token: string;
  request_count?: number;
  sort_order?: number;
}

interface RestData {
  rawId: string;
  kind: TreeKind;
  name: string;
  method?: string;
  url?: string;
  token?: string;
  requestCount?: number;
}
type RestNode = TreeItem<RestData>;

// Build the tree with folders + requests + webhooks INTERLEAVED by
// sort_order so any of them can sit between any of the others. Collapsed
// state comes from our local Set.
function buildTree(
  folders: SourceFolder[],
  requests: SourceRequest[],
  webhooks: SourceWebhook[],
  collapsed: Set<string>,
): RestNode[] {
  const fByParent = new Map<string | null, SourceFolder[]>();
  for (const f of folders) {
    const k = f.parent_folder_id ?? null;
    fByParent.set(k, [...(fByParent.get(k) ?? []), f]);
  }
  const rByParent = new Map<string | null, SourceRequest[]>();
  for (const r of requests) {
    const k = r.folder_id ?? null;
    rByParent.set(k, [...(rByParent.get(k) ?? []), r]);
  }
  const wByParent = new Map<string | null, SourceWebhook[]>();
  for (const w of webhooks) {
    const k = w.folder_id ?? null;
    wByParent.set(k, [...(wByParent.get(k) ?? []), w]);
  }

  const walk = (folderRawId: string | null): RestNode[] => {
    const fls = (fByParent.get(folderRawId) ?? []).map((f) => ({
      sort: f.sort_order ?? 0,
      build: (): RestNode => ({
        id: `f:${f.id}`,
        rawId: f.id,
        kind: "folder" as const,
        name: f.name,
        canHaveChildren: true,
        collapsed: collapsed.has(`f:${f.id}`),
        children: walk(f.id),
      }),
    }));
    const rqs = (rByParent.get(folderRawId) ?? []).map((r) => ({
      sort: r.sort_order ?? 0,
      build: (): RestNode => ({
        id: `r:${r.id}`,
        rawId: r.id,
        kind: "request" as const,
        name: r.name,
        method: r.method,
        url: r.url,
        canHaveChildren: false,
      }),
    }));
    const whs = (wByParent.get(folderRawId) ?? []).map((w) => ({
      sort: w.sort_order ?? 0,
      build: (): RestNode => ({
        id: `w:${w.id}`,
        rawId: w.id,
        kind: "webhook" as const,
        name: w.name,
        token: w.token,
        requestCount: w.request_count,
        canHaveChildren: false,
      }),
    }));
    return [...fls, ...rqs, ...whs].sort((a, b) => a.sort - b.sort).map((x) => x.build());
  };
  return walk(null);
}

export interface RenderRowArgs {
  rawId: string;
  kind: TreeKind;
  name: string;
  method?: string;
  url?: string;
  token?: string;
  requestCount?: number;
  selected: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}

export interface TreeMoveUpdate {
  rawId: string;
  kind: TreeKind;
  parentId: string | null;
  sortOrder: number;
}

export interface SortableTreeProps {
  folders: SourceFolder[];
  requests: SourceRequest[];
  webhooks?: SourceWebhook[];
  selectedRequestId: string | null;
  selectedWebhookId?: string | null;
  filter: string;
  renderRow: (args: RenderRowArgs) => ReactNode;
  onMove: (updates: TreeMoveUpdate[]) => void;
}

export default function SortableTreeComponent({
  folders, requests, webhooks = [], selectedRequestId, selectedWebhookId = null, filter, renderRow, onMove,
}: SortableTreeProps) {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const tree = useMemo(
    () => buildTree(folders, requests, webhooks, collapsed),
    [folders, requests, webhooks, collapsed],
  );
  const filterLower = filter.trim().toLowerCase();

  return (
    <LibSortableTree<RestData, HTMLDivElement>
      items={tree}
      indentationWidth={16}
      indicator
      pointerSensorOptions={{ activationConstraint: { distance: 5 } }}
      TreeItemComponent={makeTreeItem({
        renderRow,
        selectedRequestId,
        selectedWebhookId,
        filterLower,
        toggleCollapsed: (id) => setCollapsed((prev) => {
          const n = new Set(prev);
          if (n.has(id)) n.delete(id); else n.add(id);
          return n;
        }),
      })}
      onItemsChanged={(newItems, reason) => {
        if (reason.type === "collapsed" || reason.type === "expanded") {
          const id = String(reason.item.id);
          setCollapsed((prev) => {
            const n = new Set(prev);
            if (reason.type === "collapsed") n.add(id);
            else n.delete(id);
            return n;
          });
          return;
        }
        if (reason.type !== "dropped") return;
        // Walk the new tree and emit (parentId, sortOrder) for every
        // node. The caller filters to actual changes.
        const updates: TreeMoveUpdate[] = [];
        const walk = (nodes: RestNode[], parent: RestNode | null) => {
          nodes.forEach((n, idx) => {
            updates.push({
              rawId: n.rawId,
              kind: n.kind,
              parentId: parent ? parent.rawId : null,
              sortOrder: idx,
            });
            if (n.children?.length) walk(n.children as RestNode[], n);
          });
        };
        walk(newItems as RestNode[], null);
        onMove(updates);
      }}
    />
  );
}

function makeTreeItem({
  renderRow, selectedRequestId, selectedWebhookId, filterLower, toggleCollapsed,
}: {
  renderRow: SortableTreeProps["renderRow"];
  selectedRequestId: string | null;
  selectedWebhookId: string | null;
  filterLower: string;
  toggleCollapsed: (id: string) => void;
}) {
  return forwardRef<HTMLDivElement, TreeItemComponentProps<RestData>>(function RestTreeItem(
    props,
    ref,
  ) {
    const node = props.item as RestNode;
    if (filterLower) {
      const hit = node.name.toLowerCase().includes(filterLower) ||
        (node.url ?? "").toLowerCase().includes(filterLower) ||
        (node.token ?? "").toLowerCase().includes(filterLower);
      if (!hit && (node.kind === "request" || node.kind === "webhook")) return null;
    }
    const selected =
      (node.kind === "request" && node.rawId === selectedRequestId) ||
      (node.kind === "webhook" && node.rawId === selectedWebhookId);
    return (
      <FolderTreeItemWrapper
        {...props}
        ref={ref}
        // We render our own folder open/closed icon as the toggle, so
        // hide the library's chevron and click-on-row collapse.
        hideCollapseButton={true}
        disableCollapseOnItemClick={true}
      >
        {renderRow({
          rawId: node.rawId,
          kind: node.kind,
          name: node.name,
          method: node.method,
          url: node.url,
          token: node.token,
          requestCount: node.requestCount,
          selected,
          isExpanded: !node.collapsed,
          onToggle: () => toggleCollapsed(node.id as string),
        })}
      </FolderTreeItemWrapper>
    );
  });
}
